/* eslint-disable @typescript-eslint/member-ordering */

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { MatCard } from '@angular/material/card';
import { CustomerModel } from '@libs/account/models';
import { ButtonComponent, IconButtonComponent } from '@libs/common/components';
import { AuthService } from '@libs/shared/auth';
import { PriceCalculationService } from '@libs/shared/services';
import {
  CartCalculationSummaryTaxModel,
  ConfigurableBundleFormStateModel,
  ConfigurableBundleFormSummaryStateModel,
  ConfigurableBundleTemplateModel,
  ProductBundleItemModel,
} from '@libs/shop/models';
import { ProductBundleMaxOrderCountLabelComponent, ProductBundleValidToLabelComponent } from '../../../catalog';
import { ProductPriceFormatterComponent } from '../../../catalog/product-prices/product-price-formatter';
import { ConfigurableBundleFormBundleItemListComponent } from '../configurable-bundle-form-bundle-item-list';

@Component({
  selector: 'ui-configurable-bundle-form-summary',
  imports: [
    ProductPriceFormatterComponent,
    ProductBundleMaxOrderCountLabelComponent,
    ButtonComponent,
    MatCard,
    ProductBundleValidToLabelComponent,
    ConfigurableBundleFormBundleItemListComponent,
    IconButtonComponent,
  ],
  templateUrl: './configurable-bundle-form-summary.component.html',
  styleUrl: './configurable-bundle-form-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfigurableBundleFormSummaryComponent {
  private readonly authService: AuthService = inject(AuthService);
  private readonly priceCalculationService: PriceCalculationService = inject(PriceCalculationService);

  public bundleTemplate: InputSignal<ConfigurableBundleTemplateModel> = input.required();
  public bundleState: ModelSignal<ConfigurableBundleFormStateModel> = model.required();
  public addToCartDisabled: InputSignal<boolean> = input.required();
  public isAddToCartLoading: InputSignal<boolean> = input(false);
  public isInCart: InputSignal<boolean> = input.required();
  public customer: Signal<CustomerModel | undefined> = this.authService.currentCustomer;
  public listExpanded: ModelSignal<boolean> = model(false);
  public bundleItems: Signal<ProductBundleItemModel[]> = computed(() => {
    const mandatoryProducts: ProductBundleItemModel[] = this.bundleTemplate().slots
      .filter(x => x.slotType === 'mandatory')
      .map(x => x.products)
      .flat();
    const productStates: ProductBundleItemModel[] = this.bundleState().slotMap.values().toArray()
      .filter(x => x.slotType !== 'free')
      .map(x => x.itemMap.values()
        .filter(x => !!x)
        .toArray())
      .flat();

    return mandatoryProducts.concat(productStates);
  });

  public summaryState: Signal<ConfigurableBundleFormSummaryStateModel> = computed(() => {
    const products: ProductBundleItemModel[] = this.bundleItems();
    const netPrice: number = this.priceCalculationService.calculateNetPriceForProducts(products);
    const taxes: CartCalculationSummaryTaxModel[] = this.priceCalculationService.calculateTaxesForProducts(products);
    const tax: number = this.priceCalculationService.calculateTaxAmountForTaxItems(taxes);

    return {
      net: netPrice,
      taxes,
      tax,
      gross: netPrice + tax,
    };
  });

  public addToCart: OutputEmitterRef<void> = output();
}
